import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';

import axios from '../api/axios';
import { CURRENT_REGULATIONS_VERSION } from '../constants';
import type { UpdateRegulations, User, UserSettings } from '../types/user';

interface AuthContextType {
  isAuthenticated: boolean | null;
  user: User | null;
  isStatuteAccepted: boolean | null;
  login: (data: User) => void;
  logout: () => void;
  acceptRegulations: (data: UpdateRegulations) => void;
  updateCurrentUser: (data: Partial<User>) => void;
  updateUserSettings: (settings: UserSettings) => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: null,
  user: null,
  isStatuteAccepted: null,
  login() {},
  logout() {},
  acceptRegulations() {},
  updateCurrentUser() {},
  updateUserSettings() {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isStatuteAccepted, setIsStatuteAccepted] = useState<boolean | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    axios
      .get('user', { signal: controller.signal })
      .then((response) => {
        const sessionLifetime = localStorage.getItem('session_lifetime');
        // check if fetched user id is equal to user_id from local storage
        if (
          response.data.user_id.toString() === localStorage.getItem('user_id') &&
          response.data.session_id === localStorage.getItem('session_id') &&
          sessionLifetime &&
          parseInt(sessionLifetime) >= dayjs().unix()
        ) {
          setUser(response.data);
          console.debug('Fetched user data: ', response.data);
          setIsAuthenticated(true);
          localStorage.setItem('session_id', response.data.session_id);
          localStorage.setItem(
            'session_lifetime',
            dayjs().add(response.data.session_lifetime, 'seconds').unix().toString(),
          );
          localStorage.setItem('lifetime', response.data.session_lifetime);
          console.debug('User is authenticated');
        } else {
          setIsAuthenticated(false);
          console.warn('User is not authenticated');
        }
      })
      .catch((error) => {
        console.debug('Cannot fetch user data', error);
        setIsAuthenticated(false);
      });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const hasAcceptedRegulations =
      user.settings?.regulations === 'accepted' &&
      user.settings?.regulations_version === CURRENT_REGULATIONS_VERSION;
    setIsStatuteAccepted(hasAcceptedRegulations);
  }, [user]);

  const updateCurrentUser = useCallback((data: Partial<User>) => {
    setUser((prev) => (prev !== null ? { ...prev, ...data } : prev));
  }, []);

  const login = useCallback(async (data: User) => {
    setUser(data);
    setIsAuthenticated(true);
    localStorage.setItem('user_id', data.user_id.toString());
    localStorage.setItem('session_id', data.session_id);
    if (data.session_lifetime) {
      localStorage.setItem(
        'session_lifetime',
        dayjs().add(data.session_lifetime, 'seconds').unix().toString(),
      );
      localStorage.setItem('lifetime', data.session_lifetime.toString());
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user_id');
    localStorage.removeItem('session_id');
    localStorage.removeItem('session_lifetime');
    localStorage.removeItem('lifetime');
  }, []);

  const acceptRegulations = useCallback(async (data: UpdateRegulations) => {
    axios
      .post('user', data)
      .then((response) => {
        setIsStatuteAccepted(true);
        updateCurrentUser(data);
        console.debug('User settings updated', response.data);
      })
      .catch((error) => {
        console.debug('Update user settings failed', error.response.data);
        setIsStatuteAccepted(false);
      });
  }, []);

  const updateUserSettings = useCallback((settings: UserSettings) => {
    axios
      .post('user', { settings: settings })
      .then((response) => {
        updateCurrentUser({ settings: settings });
        console.debug('User settings updated', response.data);
      })
      .catch((error) => {
        console.debug('Update user settings failed', error.response.data);
      });
  }, []);

  const value = useMemo(
    () => ({
      user,
      isStatuteAccepted,
      isAuthenticated,
      updateCurrentUser,
      login,
      logout,
      acceptRegulations,
      updateUserSettings,
    }),
    [
      user,
      isStatuteAccepted,
      isAuthenticated,
      updateCurrentUser,
      login,
      logout,
      acceptRegulations,
      updateUserSettings,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
}
